import { FieldMappingFieldItem } from '@components/text-editors/addons/field-mapping';

const VARIABLE_PATTERN = /{{\s*([^}]+?)\s*}}/g;

/**
 * Converts a mapping label template from the API (e.g. "{{name}} - {{details[category]}}")
 * into a FieldMappingTextEditor-compatible default value with type-suffixed variable keys.
 *
 * Returns { value, variables } where:
 * - value: template with type-suffixed keys, e.g. "{{name_text}} - {{details[category]_text}}"
 * - variables: map from suffixed key to the matching field item
 */
export const buildMappingLabelDefaultValue = (
  mappingLabel: string,
  fields: FieldMappingFieldItem[]
): { value: string; variables: Record<string, FieldMappingFieldItem> } => {
  const getTypeSuffix = (key: string): string => {
    const field = fields.find((f) => f.label === key);
    return field?.type ?? 'text';
  };

  const value = mappingLabel.replace(
    VARIABLE_PATTERN,
    (_, key) => `{{${key}_${getTypeSuffix(key)}}}`
  );
  const variables = Array.from(mappingLabel.matchAll(VARIABLE_PATTERN)).reduce((acc, [, key]) => {
    const field = fields.find((f) => f.label === key);
    if (field) {
      acc[`${key}_${getTypeSuffix(key)}`] = field;
    }
    return acc;
  }, {} as Record<string, FieldMappingFieldItem>);

  return { value, variables };
};

/**
 * Extract path from endpoint URL by removing the base URL prefix.
 */
export const getPathFromEndpointUrl = (baseUrl: string, endpointUrl?: string): string => {
  const url = endpointUrl ?? '';
  if (!url) return '';

  const baseUrlTrimmed = baseUrl ? `${baseUrl.replace(/\/+$/, '')}/` : '';
  if (baseUrlTrimmed && url.startsWith(baseUrlTrimmed)) {
    return url.slice(baseUrlTrimmed.length);
  }
  if (baseUrl && url.startsWith(baseUrl)) {
    return url.slice(baseUrl.length).replace(/^\/+/, '');
  }
  // Fallback: if backend returned a full URL unrelated to baseUrl, or already stored a path.
  return url.replace(/^\/+/, '');
};

/**
 * Combine base URL and path into full endpoint URL.
 */
export const buildEndpointUrl = (baseUrl: string, path: string): string => {
  const baseUrlTrimmed = baseUrl ? baseUrl.replace(/\/+$/, '') : '';
  const pathTrimmed = path ? path.replace(/^\/+/, '') : '';
  return baseUrlTrimmed ? `${baseUrlTrimmed}/${pathTrimmed}` : pathTrimmed;
};
